import { User } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { ParticipantRole } from '@/types/chat.type'

interface GroupAvatarProps {
  participants: Array<{
    user: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
  }>
  size?: 'sm' | 'md' | 'lg'
  maxAvatars?: number
  ownerParticipant?: {
    role: ParticipantRole
    user: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
  }
  conversationAvatar?: string
  onClick?: () => void
  showUploadIndicator?: boolean
}

const SIZE_CLASSES = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
}

const AVATAR_SIZE_CLASSES = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
}

export const GroupAvatar: React.FC<GroupAvatarProps> = ({
  participants,
  size = 'md',
  maxAvatars = 4,
  ownerParticipant: _ownerParticipant,
  conversationAvatar,
  onClick,
  showUploadIndicator = false,
}) => {
  const activeParticipants = participants.filter(p => p.user && p.user.id)
  const displayParticipants = activeParticipants.slice(0, maxAvatars)
  const remainingCount = activeParticipants.length - maxAvatars

  if (conversationAvatar) {
    return (
      <div
        className={`${SIZE_CLASSES[size]} rounded-full bg-gray-300 flex items-center justify-center overflow-hidden ${
          onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
        }`}
        onClick={
          onClick
            ? e => {
                e.stopPropagation()
                onClick()
              }
            : undefined
        }
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onClick()
                }
              }
            : undefined
        }
      >
        <Image
          src={conversationAvatar}
          alt="Conversation avatar"
          width={size === 'sm' ? 32 : size === 'md' ? 40 : 48}
          height={size === 'sm' ? 32 : size === 'md' ? 40 : 48}
          className={`${SIZE_CLASSES[size]} rounded-full object-cover`}
        />
      </div>
    )
  }

  if (displayParticipants.length === 0) {
    return (
      <div
        className={`${SIZE_CLASSES[size]} rounded-full bg-gray-300 flex items-center justify-center ${
          onClick ? 'cursor-pointer hover:bg-gray-400 transition-colors relative group' : ''
        }`}
        onClick={
          onClick
            ? e => {
                e.stopPropagation()
                onClick()
              }
            : undefined
        }
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onClick()
                }
              }
            : undefined
        }
        title={showUploadIndicator ? 'Click to upload avatar' : undefined}
      >
        <User className={`${AVATAR_SIZE_CLASSES[size]} text-gray-600`} data-testid="user-icon" />
      </div>
    )
  }

  if (displayParticipants.length === 1) {
    const participant = displayParticipants[0]
    return (
      <div
        className={`${SIZE_CLASSES[size]} rounded-full bg-gray-300 flex items-center justify-center overflow-hidden ${
          onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
        }`}
        onClick={
          onClick
            ? e => {
                e.stopPropagation()
                onClick()
              }
            : undefined
        }
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onClick()
                }
              }
            : undefined
        }
        title={showUploadIndicator ? 'Click to upload avatar' : undefined}
      >
        {participant.user.avatar ? (
          <Image
            src={participant.user.avatar}
            alt={`${participant.user.firstName} ${participant.user.lastName}`}
            width={size === 'sm' ? 32 : size === 'md' ? 40 : 48}
            height={size === 'sm' ? 32 : size === 'md' ? 40 : 48}
            className={`${SIZE_CLASSES[size]} rounded-full object-cover`}
          />
        ) : (
          <span className={`${AVATAR_SIZE_CLASSES[size]} font-medium text-gray-600`}>
            {participant.user.firstName.charAt(0)}
            {participant.user.lastName.charAt(0)}
          </span>
        )}
      </div>
    )
  }

  // For multiple participants, create a grid layout
  const getGridConfig = (count: number) => {
    switch (count) {
      case 2:
        return 'grid-cols-2'
      case 3:
        return 'grid-cols-2'
      case 4:
        return 'grid-cols-2'
      default:
        return 'grid-cols-2'
    }
  }

  const getAvatarSize = () => {
    switch (size) {
      case 'sm':
        return { width: 16, height: 16, textSize: 'text-xs' }
      case 'md':
        return { width: 20, height: 20, textSize: 'text-sm' }
      case 'lg':
        return { width: 24, height: 24, textSize: 'text-base' }
      default:
        return { width: 20, height: 20, textSize: 'text-sm' }
    }
  }

  const avatarSize = getAvatarSize()

  return (
    <div
      className={`${SIZE_CLASSES[size]} rounded-full bg-gray-100 overflow-hidden border border-gray-200 ${
        onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
      }`}
      onClick={
        onClick
          ? e => {
              e.stopPropagation()
              onClick()
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                onClick()
              }
            }
          : undefined
      }
      title={showUploadIndicator ? 'Click to upload avatar' : undefined}
    >
      <div className={`h-full ${getGridConfig(displayParticipants.length)} grid`}>
        {displayParticipants.map(participant => (
          <div key={participant.user.id} className="relative">
            {participant.user.avatar ? (
              <Image
                src={participant.user.avatar}
                alt={`${participant.user.firstName} ${participant.user.lastName}`}
                width={avatarSize.width}
                height={avatarSize.height}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                <span className={`${avatarSize.textSize} font-medium text-gray-600`}>
                  {participant.user.firstName.charAt(0)}
                  {participant.user.lastName.charAt(0)}
                </span>
              </div>
            )}
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="bg-blue-500 flex items-center justify-center">
            <span className={`${avatarSize.textSize} font-medium text-white`}>+{remainingCount}</span>
          </div>
        )}
      </div>
    </div>
  )
}
